import { ChangeDetectorRef, Component, HostBinding, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import * as _ from 'lodash';
import { interval, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { isObject } from '../../models/utils';
import { Cluster } from '../../models/cluster';
import { Comparison } from '../../models/comparison';
import { ContextMenuOption } from '../../models/context-menu-option';
import { DrRequestInfo } from '../../models/dr-request-info';
import { ClusterModalType, Endpoint, EntityType, HelpIndex, MessageType, UpdateStatusType } from '../../models/enums';
import { CellActionType, GenericColDef, GridContext, GridRowSelectionType } from '../../models/grid-context';
import { NavigationContentComponent } from '../../models/navigation-content-component';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { DocumentListComponent } from '../document-list/document-list.component';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { CreateManifestModalComponent } from '../modals/create-manifest/create-manifest.component';
import { SummaryData, SummaryInfo } from '../summary/summary-info/summary-info';
import { Permissions, ObjectActionListType } from '../../models/permission';
import { BuildClustersModalComponent } from '../modals/build-clusters-modal/build-clusters-modal.component';
import { OperationState, TaskStatus } from '../../models/task';

@Component({
    standalone: false,
    selector: 'res-cluster-list',
    templateUrl: './cluster-list.component.html',
    styleUrls: ['./cluster-list.component.scss']
})
export class ClusterListComponent implements NavigationContentComponent, OnInit, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent) genericGrid: GenericGridComponent;
    @ViewChild(DocumentListComponent, { static: true }) documentList: DocumentListComponent;
    @ViewChild('headerTemplate', { static: true }) headerTemplate: TemplateRef<any>;
    summaryData: SummaryData;
    selectedNode: GenericTreeNode;
    selectedTreeItem: GenericTreeNode;

    gridName: string;
    automationId: string;
    updateStatusType: UpdateStatusType;

    columnDefs: Array<GenericColDef>;
    requestInfo: DrRequestInfo;
    gridContext: GridContext;

    generateButtonVisible: boolean;
    rootView: any;
    parentView: any;
    updateButtonEnabled: boolean;
    rebuildButton: boolean;
    requestInfoCluster: DrRequestInfo;
    generateReportVisible: boolean;
    restoreClusterAfterDocViewerClosed = null;

    isBusyLoading = false;
    isBusyLoadingMessage: string;
    breadcrumbs: Array<any>;
    currentColumns: Array<GenericColDef>;
    onExpandCollapseIconClick = false;

    private selectedCluster: GenericTreeNode;
    private refreshClusterRequestEvent: Subscription;
    private currentColumnsEvent: Subscription;

    currentClusterViewHandle: string;
    currentClusterViewID: string;
    currentClusterTaskHandle: string;

    // clusters are out of date
    clustersOutofDate = false;
    outofDateClusterViewName = '';

    activeClusterPoller = null;

    /* istanbul ignore next */
    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Build Subclusters ...', iconClass: 'fa-plus', iconColor: 'color-green-04', automationId: 'cluster-doclist-build-subclusters',
            click: (item) => this.openBuildSubcluster(item),
            enabled: (item) => item && item.documentCount > 10,
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'Create Manifest ...', iconClass: 'fa-list-alt', iconColor: 'color-blue-01', automationId: 'cluster-doclist-create-manifest',
            click: (item) => this.createManifest(item)
        })
    ];
    /* istanbul ignore next */
    responseCallback = (response: any) => {
        const viewAttributes = response?.view?.attributes;
        // check to see if the cluster view is out dated
        this.isClusterViewOutDated(viewAttributes, response.view.name);
        if (response.clusterViews && !response.childViews) {
            response.childViews = response.clusterViews;
        }
        if (response.childViews) {
            if (response.view) {
                this.parentView.childViewCount = response.view.childViewCount;
                if (this.parentView?.attributes && response.view.attributes) {
                    const clusterLevel = response.view.attributes.find(a => a.name === 'clusterViewLevel');
                    if (clusterLevel) {
                        this.parentView.attributes.push(clusterLevel);
                    }
                }
            }
        }
    }

    isClusterViewOutDated = (attributes, viewName) => {
        if (attributes?.length > 0) {
            const clusterState = attributes.find((attr) => attr.name === 'clusterstate');
            if (clusterState?.value === 'OUT_OF_DATE') {
                this.outofDateClusterViewName = viewName;
                this.clustersOutofDate = true;
            } else {
                this.clustersOutofDate = false;
            }
            this.updateButtonEnabled = this.clustersOutofDate;
        }
    }
    /* istanbul ignore next */
    loadDataFinishedCallback = () => {
        const selectedRow = this.genericGrid.gridApi?.getSelectedRows();
        if (selectedRow || selectedRow.length === 0) {
            let selectedNode = null;
            if (this.currentClusterViewHandle) {
                selectedNode = this.genericGrid.findNode((node: RowNode) => node.data?.handle === this.currentClusterViewHandle, true);
                this.currentClusterViewHandle = null;
            }
            if (!selectedNode) {
                const firstNode = this.genericGrid.gridApi?.getRowNode('0');
                if (firstNode) {
                    firstNode.setSelected(true);
                    this.selectedCluster = firstNode.data as GenericTreeNode;
                    this.documentList.selectedTreeItemChanged(new GenericTreeNode({ data: firstNode }));
                }
            }
        }

        if (this.genericGrid && this.parentView) {
            this.genericGrid.totalItems = this.parentView.childViewCount;
        }
    }
    /* istanbul ignore next */
    cellClickedCallback = (rowNode: RowNode, action: CellActionType): Promise<any> => {
        if (rowNode && rowNode.data && rowNode.data.handle && action) {
            switch (action) {
                case CellActionType.drillCluster: {
                    const rowNodeData = rowNode.data;
                    this.onExpandCollapseIconClick = true;
                    if (rowNode.data instanceof Cluster) {
                        return new Promise<any>((resolve) => {
                            this.restService.post(Endpoint.clusterViewManager_listChildViews, {
                                parentClusterViewHandle: rowNodeData.listingHandle,
                                count: 100,
                                startIndex: 0
                            }).then(response => {
                                response.childViews = (<Array<any>>response.childViews).map(c => new Cluster(c));
                                const childViews = (<Array<any>>response.childViews).map(c => {
                                    c.isParent = c.childViewCount > 0 ? true : false;
                                    c.hasMultiLevelGroups = true;
                                    c.children = [];
                                    return new Cluster(c);
                                });
                                rowNode.data.children = childViews;
                                rowNode.data.isParent = true;
                                rowNode.data.hasMultiLevelGroups = true;
                                this.genericGrid.expandCollapseRow(rowNode);
                                resolve(rowNode);
                            });
                        });
                    } else {
                        return Promise.resolve(true);
                    }
                }
            }
        }
    }
    /* istanbul ignore next */
    onSelectionChanged = (event) => {
        const cluster: Cluster = event.api.getSelectedRows()[0];
        if (cluster) {
            this.commonService.currentClusterView = this.selectedCluster = new GenericTreeNode({
                name: cluster.name,
                data: cluster,
                entityType: EntityType.clusterView,
                bundle: { breadcrumbs: this.breadcrumbs }
            });
            if (this.currentClusterViewID && this.currentClusterViewID.split('-').length > 1 && this.restoreClusterAfterDocViewerClosed) {
                this.restoreClusterAfterDocViewerClosed = false;
                const splitID = this.currentClusterViewID.split('-', 1).join('-');
                this.expandGridRows(splitID);
            }
            else {
                if (this.commonService?.expandedClusterView?.clusterID && this.commonService.expandedClusterView?.isClusterExpanded && this.commonService.expandedClusterView?.clusterID?.split('-').length === 1) {
                    this.expandGridRows(this.commonService.expandedClusterView.clusterID);
                }
                this.documentList.selectedTreeItemChanged(this.selectedCluster);
            }
        }
    }

    expandGridRows = (id: string): Promise<any> => {
        const allRows = this.genericGrid.getRows();
        const index = allRows.findIndex(row => row.data.clusterID === id);
        if (index !== -1) {
            return new Promise<any>((resolve) => {
                this.cellClickedCallback(allRows[index], CellActionType.drillCluster).then((cluster) => {
                    resolve(cluster);
                    // if the currentClusterViewID is available in the cluster or its children, select it
                    let rowIndex: number;
                    let currentCluster: Cluster;
                    let gridRows: RowNode[];
                    const foundClusterinChildren = cluster.data.children.find(childCluster => {
                        return childCluster.clusterID === this.currentClusterViewID;
                    });

                    if (cluster.data.clusterID === this.currentClusterViewID || foundClusterinChildren) {
                        currentCluster = foundClusterinChildren ? foundClusterinChildren : cluster;
                        gridRows = this.genericGrid.getRows();
                        rowIndex = gridRows.findIndex(row => row.data.clusterID === currentCluster.clusterID);
                        this.genericGrid.selectNode(gridRows[rowIndex]);
                        // after selecting the node see if the node is expanded, when returning from doc-viewer
                        // if yes, expand the selected node
                        if (this.commonService.expandedClusterView.clusterID === gridRows[rowIndex].data.clusterID && this.commonService.expandedClusterView.isClusterExpanded) {
                            this.expandGridRows(gridRows[rowIndex].data.clusterID);
                        }
                    } else {
                        const idSplitlength = id.split('-').length;
                        if (this.currentClusterViewID.split('-').length > idSplitlength) {
                            const subClusterID = this.currentClusterViewID.split('-', idSplitlength + 1).join('-');
                            this.expandGridRows(subClusterID);
                        }
                    }
                });
            });
        }
    }

    constructor(public changeDetector: ChangeDetectorRef, private commonService: CommonService, private restService: RestService) {
        this.currentColumns = new Array<GenericColDef>();
        this.refreshClusterRequestEvent = this.commonService.getMessage(MessageType.refreshCluster).subscribe((data) => {
            if (data?.taskStatus && data?.handle) {
                if (data.handle === this.currentClusterTaskHandle) {
                    if (data.taskStatus === TaskStatus.FAILURE) {
                        this.isBusyLoading = false;
                    }
                    if (data.taskStatus === TaskStatus.SUCCESS) {
                        this.loadClusterRootView();
                    }
                    this.currentClusterTaskHandle = null;
                } else {
                    this.loadClusterRootView();
                }
            } else {
                this.initializeData();
                if (this.genericGrid) {
                    this.genericGrid.setData([]);
                }
                if (this.documentList && this.documentList.genericGrid) {
                    this.documentList.genericGrid.setData([]);
                }
            }
        });
        this.currentColumnsEvent = this.commonService.getMessage(MessageType.currentColumns).subscribe((data) => {
            if (data) {
                this.currentColumns = data;
            }
        });
    }

    onRowClickedCallback = (event) => {
        if (!this.onExpandCollapseIconClick) {
            event.node.setSelected(true);
        } else {
            this.onExpandCollapseIconClick = false;
        }
    }

    identify(index: number, item: any) {
        return item.name;
    }

    /* istanbul ignore next */
    ngOnInit(): void {
        this.initializeData();
    }
    /* istanbul ignore next */
    initializeData() {
        if (this.commonService.currentClusterView) {
            this.currentClusterViewHandle = this.commonService.currentClusterView.data.handle;
            this.currentClusterViewID = this.commonService.currentClusterView.data.clusterID;
            if (this.commonService.currentClusterView.bundle) {
                this.restoreClusterAfterDocViewerClosed = this.commonService.currentClusterView.bundle;
            }
        }
        this.commonService.currentClusterView = null;
        this.generateReportVisible = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_REPORTS);
        this.parentView = null;
        this.rootView = null;
        this.breadcrumbs = [];
    }
    /* istanbul ignore next */
    selectedTreeItemChanged = (node: GenericTreeNode) => {
        this.generateButtonVisible = false;
        this.selectedTreeItem = node;
        if (node && node.data && node.entityType) {
            if (this.parentView && this.parentView.isClustered && this.parentView.listingHandle) {
                this.loadClusters();
            } else if (!this.rootView || !this.rootView.isClustered) {
                this.loadClusterRootView();
                this.isBusyLoading = true;
                this.isBusyLoadingMessage = 'Loading ...';
            }
        } else {
            this.isBusyLoading = false;
        }
    }

    startClusterPoller(): void {
        const pollInterval = 30000; // 30 sec
        if (!this.activeClusterPoller) {
            this.activeClusterPoller = interval(pollInterval).pipe(startWith(0)
            ).subscribe(() => this.loadClusterRootView());
        }
    }

    stopClusterPoller(): void {
        if (this.activeClusterPoller) {
            this.activeClusterPoller.unsubscribe();
            this.activeClusterPoller = null;
        }
    }

    /* istanbul ignore next */
    loadClusterRootView(): void {

        let view = this.selectedTreeItem.data;
        if (this.selectedTreeItem.entityType === EntityType.comparison) {
            this.selectedTreeItem.data = this.commonService.selectedComparisonNavigation;
        }

        if (this.selectedTreeItem.data?.view) {
            view = this.selectedTreeItem.data.view;
        } else if (this.selectedTreeItem.data instanceof Comparison) {
            const comparisonView = _.filter(this.selectedTreeItem.data, (viewItem) => viewItem && viewItem.handle === this.selectedTreeItem.data.docListHandle);
            if (comparisonView && comparisonView.length > 0) {
                view = comparisonView[0];
            }
        }

        if (view?.handle) {
            this.restService.post(Endpoint.viewManager_getView, { handle: view.handle, selectedAttributes: ['clusterviewhandle', 'clusteringstate'] }).then(responseGetView => {
                if (responseGetView && responseGetView.view) {
                    this.rootView = responseGetView.view;
                    this.rootView.isClustered = false;
                    this.breadcrumbs = [{ name: 'All Clusters', view: this.rootView }];

                    if (this.rootView.attributes) {
                        let att = _.filter(this.rootView.attributes, (cluster) => cluster.name === 'clusterviewhandle');
                        if (att && att.length > 0) {
                            this.rootView.listingHandle = att[0].value;
                        }

                        att = _.filter(this.rootView.attributes, (state) => state.name === 'clusteringstate');
                        const taskHandle = this.rootView.attributes.find(a => a.name === 'clusteringtaskhandle');
                        if (att && att.length > 0) {
                            switch (att[0].value?.toLowerCase()) {
                                case 'complete':
                                    this.rootView.isClustered = true;
                                    this.generateButtonVisible = false;
                                    this.currentClusterTaskHandle = null;
                                    if (this.genericGrid) {
                                        this.genericGrid.suppressRefresh = false;
                                    }
                                    this.stopClusterPoller();
                                    break;
                                case 'in_progress':
                                    this.isBusyLoadingMessage = `Clustering ${this.rootView.name} ...`;
                                    this.currentClusterTaskHandle = taskHandle ? taskHandle.value : null;
                                    this.startClusterPoller();
                                    break;
                                case 'out_of_date':
                                    this.rootView.isClustered = true;
                                    this.updateButtonEnabled = true;
                                    if (this.genericGrid) {
                                        this.genericGrid.suppressRefresh = false;
                                    }
                                    this.stopClusterPoller();
                                    break;
                                case 'none':
                                    this.generateButtonVisible = true;
                                    if (this.genericGrid) {
                                        this.genericGrid.suppressRefresh = false;
                                    }
                                    this.stopClusterPoller();
                                    break;
                            }
                        } else {
                            this.generateButtonVisible = true;
                            this.isBusyLoading = false;
                        }
                    } else {
                        this.generateButtonVisible = true;
                        this.isBusyLoading = false;
                    }

                    if (this.rootView.isClustered) {
                        this.parentView = this.rootView;
                        this.rebuildButton = true;

                        if (this.parentView && this.parentView.isClustered && this.parentView.listingHandle) {
                            if (this.restoreClusterAfterDocViewerClosed?.breadcrumbs?.length > 1 && this.documentList?.requestInfo?.data?.viewHandle !== this.restoreClusterAfterDocViewerClosed.clusterHandle) {
                                const childrenLength = this.restoreClusterAfterDocViewerClosed.breadcrumbs.length;

                                this.navigateCluster(this.restoreClusterAfterDocViewerClosed.breadcrumbs[childrenLength - 1]);

                                this.currentClusterViewHandle = this.restoreClusterAfterDocViewerClosed.clusterHandle;
                                this.breadcrumbs = this.restoreClusterAfterDocViewerClosed.breadcrumbs;
                                this.restoreClusterAfterDocViewerClosed = null;
                            } else {
                                this.parentView.entityType = EntityType.cluster;
                                this.loadClusters();
                            }
                        }
                    } else {
                        this.isBusyLoading = this.generateButtonVisible ? false : true;
                    }
                }
            }, error => {
                this.generateButtonVisible = true;
                this.isBusyLoading = false;
                this.stopClusterPoller();
            });
        } else {
            this.generateButtonVisible = true;
            this.isBusyLoading = false;
        }
    }

    getCurrentSubclusterLevelsOfTheView(view = null): number {
        const clusterViewAttribs = view?.attributes?.length > 0 ? view.attributes : this.rootView?.attributes;
        if (clusterViewAttribs) {
            const levelsAttribute = clusterViewAttribs.find(a => a.name === 'clusterViewLevel');
            if (levelsAttribute) {
                return parseInt(levelsAttribute.value);
            }
        }
        return 2;
    }
    /* istanbul ignore next */
    loadClusters(): void {
        if (this.parentView && this.parentView.entityType && SummaryInfo.has(this.parentView.entityType)) {
            this.summaryData = SummaryInfo.get(this.parentView.entityType)(this.parentView);
            Object.assign(this, this.summaryData);
            if (this.summaryData.isValid) {
                this.selectedTreeItem = null;
                this.changeDetector.detectChanges();
                this.selectedTreeItem = this.parentView;
                this.genericGrid.loadData();
                this.isBusyLoading = false;
            }
        }
    }

    openBuildSubcluster(clusterView = null): void {
        this.commonService.openModal(BuildClustersModalComponent, { modalData: { clusterModalType: ClusterModalType.Subclusters, subclusterLevels: this.getCurrentSubclusterLevelsOfTheView(clusterView) }, class: 'horizontal-px-400' }).then(response => {
            if (response.modalResult && response.modalData) {
                if (clusterView?.childViewCount > 0) {
                    this.isBusyLoading = true;
                    this.isBusyLoadingMessage = 'Clustering ' + clusterView.name + ' ...';
                    this.restService.post(Endpoint.clusterViewManager_uncluster, {
                        viewHandle: clusterView.handle,
                        taskDescription: 'Uncluster ' + clusterView.name,
                    }).then(() => {
                        this.createSubcluster(clusterView, parseInt(response.modalData.subclusterLevels));
                    }, () => {
                        this.isBusyLoading = false;
                    });
                } else {
                    this.createSubcluster(clusterView, parseInt(response.modalData.subclusterLevels));
                }
            }
        });
    }

    openBuildCluster(): void {
        this.commonService.openModal(BuildClustersModalComponent, { modalData: { clusterModalType: ClusterModalType.Build, subclusterLevels: this.getCurrentSubclusterLevelsOfTheView() }, class: 'horizontal-px-400' }).then(response => {
            if (response.modalResult && response.modalData) {
                this.generateClusterList(parseInt(response.modalData.subclusterLevels));
            }
        });
    }

    openRebuildCluster(): void {
        this.commonService.openModal(BuildClustersModalComponent, { modalData: { clusterModalType: ClusterModalType.Rebuild, subclusterLevels: this.getCurrentSubclusterLevelsOfTheView() }, class: 'horizontal-px-400' }).then(response => {
            if (response.modalResult && response.modalData) {
                this.rebuildAll(parseInt(response.modalData.subclusterLevels));
            }
        });
    }

    openGenerateClusterReport(): void {
        this.commonService.openModal(BuildClustersModalComponent, { modalData: { clusterModalType: ClusterModalType.Generate, subclusterLevels: this.getCurrentSubclusterLevelsOfTheView() }, class: 'horizontal-px-400' }).then(response => {
            if (response.modalResult && response.modalData && this.rootView) {
                this.restService.post(Endpoint.clusterViewManager_generateClusterReport, {
                    viewHandle: this.rootView.listingHandle, maxClustersPerLevel: 10, maxLevels: 1, subclusterLevel: parseInt(response.modalData.subclusterLevels), minDocumentsPerCluster: 10,
                    taskDescription: 'Generate cluster report ' + this.rootView.name, createTopLevelClusters: true
                });
                this.commonService.openInformationToastNotification('Generation of the Cluster Report has started. Once finished, the report can be downloaded from the Work Basket.');
            }
        });
    }

    /* istanbul ignore next */
    generateClusterList(subclusterLevels: number): void {
        if (this.rootView) {
            this.generateButtonVisible = false;
            this.isBusyLoading = true;
            this.isBusyLoadingMessage = `Clustering  ${this.rootView.name} ... `;
            const cView = _.cloneDeep(this.rootView);
            const cViewName = cView.name === 'Project Data' ? 'CaseData' : cView.name;
            this.restService.post(Endpoint.workingSetManager_createClusterView, {
                sourceViewHandle: cView.handle,
                workingSetHandle: this.commonService.projectData.handle,
                name: cViewName,
                attributes: [{ name: 'parentviewhandle', value: cView.handle }, { name: 'sethandle', value: cView.handle }],
                locked: true
            }).then(response => {
                if (response && response.handle) {
                    this.restService.post(Endpoint.workingSetManager_createClusterViewResult, {
                        handle: response.handle,
                    }).then(responseClusterView => {
                        if (responseClusterView && responseClusterView.clusterView) {
                            this.restService.post(Endpoint.clusterViewManager_cluster, {
                                viewHandle: responseClusterView.clusterView.handle, maxClustersPerLevel: 10, maxLevels: 1, subclusterLevel: subclusterLevels, minDocumentsPerCluster: 10,
                                taskDescription: 'Cluster view ' + cView.name, createTopLevelClusters: true
                            }).then((responseCluster) => {
                                if (responseCluster) {
                                    this.currentClusterTaskHandle = responseCluster.taskHandle;
                                }
                                this.isBusyLoading = false;
                                this.rootView.entityType = EntityType.cluster;
                                this.selectedTreeItem.data = this.rootView;
                                this.selectedTreeItemChanged(this.selectedTreeItem);

                            }, () => {
                                this.isBusyLoading = false;
                            });
                        } else {
                            this.generateButtonVisible = true;
                            this.isBusyLoading = false;
                        }
                    });
                } else {
                    this.generateButtonVisible = true;
                    this.isBusyLoading = false;
                }
            });
        }
    }
    /* istanbul ignore next */
    updateCluster(): void {
        if (this.rootView && this.rootView.listingHandle) {
            this.isBusyLoading = true;
            this.isBusyLoadingMessage = 'Updating clusters on view ' + this.rootView.name + '...';
            this.restService.post(Endpoint.clusterViewManager_updateCluster, {
                viewHandle: this.rootView.listingHandle,
                taskDescription: 'Update clusters on view ' + this.rootView.name,
            }).then(response => {
                this.parentView = this.rootView = null;
                this.isBusyLoading = false;
                this.selectedTreeItem.data = this.selectedTreeItem;
                this.selectedTreeItemChanged(this.selectedTreeItem);
            }, error => {
                this.isBusyLoading = false;
            });
        }
    }
    /* istanbul ignore next */
    createSubcluster(item: any, subclusterLevels: number): void {
        if (item) {
            this.isBusyLoading = true;
            this.isBusyLoadingMessage = 'Clustering ' + item.name + ' ...';
            this.restService.post(Endpoint.clusterViewManager_cluster, {
                viewHandle: item.handle, maxClustersPerLevel: 10, maxLevels: 1, subclusterLevel: subclusterLevels, minDocumentsPerCluster: 10,
                taskDescription: 'Cluster ' + item.name, createTopLevelClusters: false
            }).then(response => {
                this.isBusyLoading = false;
                this.selectedTreeItem.data = this.selectedTreeItem;
                this.selectedTreeItemChanged(this.selectedTreeItem);
                this.loadClusterRootView();
            }, error => {
                this.isBusyLoading = false;
            });
        }
    }

    /* istanbul ignore next */
    createManifest(item): void {
        this.commonService.openModal(CreateManifestModalComponent, {
            modalData: {
                entity: item,
                defaultColumns: this.currentColumns,
            }, class: 'vertical-percent-100 horizontal-percent-100',
            backdrop: false, keyboard: false
        });
    }
    /* istanbul ignore next */
    rebuildAll(subclusterLevels: number): void {
        if (this.rootView?.listingHandle) {
            this.isBusyLoading = true;
            this.isBusyLoadingMessage = `Clustering ${this.rootView.name} ...`;
            this.restService.post(Endpoint.clusterViewManager_uncluster, {
                viewHandle: this.rootView.listingHandle,
                taskDescription: 'Rebuild cluster on view ' + this.rootView.name,
            }).then(response => {
                if (this.documentList?.genericGrid) {
                    this.documentList.genericGrid.suppressRefresh = true;
                }
                if (this.genericGrid) {
                    this.genericGrid.suppressRefresh = true;
                }
                this.breadcrumbs = [{ name: 'All Clusters', view: this.rootView }];
                this.restService.post(Endpoint.clusterViewManager_cluster, {
                    viewHandle: this.rootView.listingHandle, maxClustersPerLevel: 10, maxLevels: 1, subclusterLevel: subclusterLevels, minDocumentsPerCluster: 10,
                    taskDescription: 'Cluster view ' + this.rootView.name, createTopLevelClusters: true
                }).then(responseCluster => {
                    if (responseCluster?.taskHandle) {
                        this.currentClusterTaskHandle = responseCluster.taskHandle;
                    }
                    this.parentView = this.rootView;
                    this.parentView.data = this.rootView;
                    this.parentView.entityType = EntityType.cluster;

                    this.selectedTreeItemChanged(this.parentView);
                    this.loadClusterRootView();
                    this.isBusyLoading = false;
                    if (this.genericGrid) {
                        this.genericGrid.suppressRefresh = false;
                    }
                    this.documentList.genericGrid.suppressRefresh = false;
                }, () => {
                    this.isBusyLoading = false;
                    this.documentList.genericGrid.suppressRefresh = false;
                    this.currentClusterTaskHandle = null;
                    if (this.genericGrid) {
                        this.genericGrid.suppressRefresh = false;
                    }
                });
            }, () => {
                this.isBusyLoading = false;
                this.documentList.genericGrid.suppressRefresh = false;
            });
        }
    }
    /* istanbul ignore next */
    navigateCluster(link): void {
        if (isObject(link) && isObject(link.view)) {
            this.parentView = link.view;
            if (!this.parentView.data) {
                this.parentView.data = link.view;
            }
            this.parentView.entityType = EntityType.cluster;
            this.selectedTreeItemChanged(this.parentView);
            this.breadcrumbs.length = this.breadcrumbs.indexOf(link) + 1;
        }
    }
    /* istanbul ignore next */
    getTitle(breadcrumb): string {
        if (breadcrumb) {
            return breadcrumb.name;
        }
        return null;
    }

    openHelp = () => {
        this.commonService.openHelp(HelpIndex.myedisc_clusters_tab_dlg);
    }

    ngOnDestroy(): void {
        this.refreshClusterRequestEvent.unsubscribe();
        this.currentColumnsEvent.unsubscribe();
        if (!this.commonService.documentViewerOpenContext && this.commonService.currentClusterView) {
            this.commonService.currentClusterView.bundle = null;
            this.commonService.expandedClusterView = null;
        }
        this.stopClusterPoller();
    }
}
